import React from 'react';
import { Link } from 'react-router-dom';
import './CombatVetPTSDPage.css';

function CombatVetPTSDPage() {
  const bannerStyle = {
    backgroundImage: `url(/assets/nexus_aegis_hero_banner.webp)`,
  };

  return (
    <main className="cvp-page">
      <section className="cvp-banner" style={bannerStyle}>
        <div className="cvp-banner-overlay">
          <Link to="/" className="cvp-back">&larr; Home</Link>
          <img
            src="/assets/nexus_aegis_logo.webp"
            alt="NeXus Aegis"
            className="cvp-banner-logo cvp-banner-logo--aegis"
          />
          <p className="cvp-eyebrow">Flagship Product</p>
          <h1 className="cvp-banner-title">
            NeXus Aegis
            <span className="cvp-banner-title-sub">Veteran PTSD.</span>
          </h1>
          <p className="cvp-banner-deck">
            A treatment-monitoring engine for the clinicians who<br />
            hold the room when a veteran cannot yet name what they carry home.
          </p>
        </div>
      </section>

      <header className="cvp-header">
        <p className="cvp-deck">
          NeXus Aegis is where NeXus NeuroInformatics deploys first. It is
          the product line that anchors our regulatory story, our Department
          of Veterans Affairs and Department of Defense integration strategy,
          and our clinical development pipeline. Every other product on our
          roadmap is built on architecture proven in this one.
        </p>
      </header>

      <section className="cvp-section">
        <span className="cvp-label">What it is</span>
        <h2 className="cvp-h2">A clinician-facing monitoring tool. Not a diagnostic. Not a consumer app.</h2>
        <p>
          Our clinician sees the veteran across an active care relationship.
          They administer validated assessments. They order laboratory
          panels. They collect patient-reported outcomes. What they have not
          had, until now, is a longitudinal, structured view of the
          neurochemical state their patient is actually in during treatment.
        </p>
        <p>
          NeXus is engineered to give them that view. Not to replace their
          judgment. Not to diagnose the patient. To surface the biology
          underneath the symptoms they are already tracking, and to write it
          back into the record where they already work.
        </p>
      </section>

      <section className="cvp-section">
        <span className="cvp-label">Architecture</span>
        <h2 className="cvp-h2">One engine. Three off-the-shelf validated inputs.</h2>
        <p>
          At the core sits our Neurochemical Language Model, built on the
          pharmacokinetic decay architecture protected under United States
          provisional patents 63/962,385 and 63/939,190. The engine consumes
          three inputs, each drawn from validated instruments already
          accepted in clinical practice:
        </p>

        <ul className="cvp-list">
          <li>
            <strong>CNSR biomarker panel.</strong> A CAP-accredited
            blood-based neurochemical assay panel. Standard laboratory
            requisition. Results flow into the engine as HL7 FHIR Observation
            resources.
          </li>
          <li>
            <strong>Patient-reported outcome instrument.</strong> Validated
            PRO tools such as PROMIS Emotional Distress and PTSDialogue,
            delivered through the existing patient-portal or in-visit
            workflow.
          </li>
          <li>
            <strong>Clinician-administered assessment.</strong> CAPS-5
            E-subscale or equivalent, performed by the treating clinician as
            part of routine monitoring visits.
          </li>
        </ul>

        <p>
          NeXus does not attempt to qualify these instruments. They are
          validated in the field already. The engine is what is novel. The
          output is a longitudinal neurochemical trajectory, written back
          into the patient chart as FHIR resources so it appears where the
          clinician already looks.
        </p>
      </section>

      <section className="cvp-section">
        <span className="cvp-label">Integration</span>
        <h2 className="cvp-h2">EHR-native by design. Built against HL7 FHIR from day one.</h2>
        <p>
          Our first integration lane targets the United States Department of
          Veterans Affairs through the VA Lighthouse Clinical Health API,
          which exposes veteran chart data in HL7 FHIR format. The tool reads
          existing chart data and writes structured monitoring output back
          into the same chart. No new dashboard. No new login. No context
          switch away from the record the clinician already works in.
        </p>
        <p>
          Runtime is stateless. Patient-identifiable data lives in the
          chart, not on NeXus infrastructure. Model improvement uses
          aggregated de-identified corpora through a Predetermined Change
          Control Plan consistent with United States Food and Drug
          Administration guidance for continuously-learning software as a
          medical device.
        </p>
      </section>

      <section className="cvp-section">
        <span className="cvp-label">Evidence</span>
        <h2 className="cvp-h2">Grounded in a PRISMA-compliant systematic literature review.</h2>
        <p>
          The neurochemistry that grounds the engine is drawn from a
          PRISMA-compliant systematic literature review of combat-related
          PTSD, with citation-level provenance carried through every stage
          of model development. Every training row carries source,
          session, and line-level citation. There is no synthetic training
          data in the corpus.
        </p>
      </section>

      <section className="cvp-cta-block">
        <p className="cvp-cta-label">Enter the product site</p>
        <h2 className="cvp-cta-title">Two doors. One product.</h2>
        <p className="cvp-cta-lede">
          The NeXus Aegis product site is built with two entrances,
          because a treatment-monitoring tool serves two audiences at once.
          Both live at <strong>ptsd.nexusneurodata.com</strong>.
        </p>

        <div className="cvp-cta-buttons">
          <a
            href="https://ptsd.nexusneurodata.com/for-clinicians"
            target="_blank"
            rel="noopener noreferrer"
            className="cvp-cta-button cvp-cta-button--primary"
          >
            For Clinicians &rarr;
          </a>
          <a
            href="https://ptsd.nexusneurodata.com/our-vets"
            target="_blank"
            rel="noopener noreferrer"
            className="cvp-cta-button cvp-cta-button--secondary"
          >
            Our Vets &rarr;
          </a>
        </div>
      </section>

      <section className="cvp-footer-note">
        <p>
          NeXus Aegis is investigational. It is not FDA-cleared,
          approved, or authorized. It does not diagnose or treat PTSD. It is a
          monitoring instrument for use by licensed clinicians as an adjunct
          to standard-of-care treatment. See the{' '}
          <Link to="/qa">Q&amp;A</Link> for the full regulatory scope.
        </p>
      </section>
    </main>
  );
}

export default CombatVetPTSDPage;
